import React from "react";
import { CheckCircle2, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const plans = [
  {
    name: "Starter",
    price: "Free",
    period: "",
    tagline: "For getting your search off the ground",
    features: [
      "Apply to 3 jobs/day",
      "Create your public profile",
      "Basic job recommendations",
    ],
    accent: "from-slate-400 to-slate-500",
    badge: "bg-slate-100 text-slate-600",
    button: "bg-slate-900 hover:bg-slate-800",
    popular: false,
  },
  {
    name: "Pro",
    price: "₹199",
    period: "/month",
    tagline: "For candidates actively interviewing",
    features: [
      "Unlimited job applications",
      "Priority recommendations",
      "Access to recruiter views",
      "Earn coins for actions",
    ],
    accent: "from-blue-500 to-indigo-600",
    badge: "bg-blue-50 text-blue-600",
    button: "bg-blue-600 hover:bg-blue-700",
    popular: true,
  },
  {
    name: "Elite",
    price: "₹399",
    period: "/month",
    tagline: "For a fully guided, fast-tracked search",
    features: [
      "All Pro benefits",
      "1:1 career consultation",
      "Early access to top jobs",
      "Job tracking dashboard",
      "Exclusive community access",
    ],
    accent: "from-purple-500 to-fuchsia-600",
    badge: "bg-purple-50 text-purple-600",
    button: "bg-purple-600 hover:bg-purple-700",
    popular: false,
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  return (
    <section className="bg-gray-50 py-20 px-6">
      <div className="max-w-7xl mx-auto text-center">
        <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
          <Sparkles className="w-3.5 h-3.5" />
          Pricing
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
          Simple pricing, powerful value
        </h2>
        <p className="text-gray-600 mb-12 max-w-xl mx-auto">
          Choose a plan that fits your job-seeking journey. Upgrade or cancel anytime.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative flex flex-col text-left rounded-2xl bg-white border p-8 transition-all ${
                plan.popular
                  ? "border-blue-200 shadow-xl shadow-blue-600/10 md:-translate-y-3"
                  : "border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1"
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                  <Sparkles className="w-3 h-3" />
                  Most Popular
                </span>
              )}

              <div className={`h-1.5 w-12 rounded-full bg-gradient-to-r ${plan.accent} mb-6`} />

              <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
              <p className="text-sm text-gray-500 mb-6">{plan.tagline}</p>

              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                {plan.period && <span className="text-gray-500 text-sm">{plan.period}</span>}
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate("/pricing")}
                className={`w-full py-3 px-4 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm ${plan.button}`}
              >
                Choose {plan.name}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
